import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Avatar,
  Divider,
} from '@mui/material';
import { CheckCircle, HourglassEmpty } from '@mui/icons-material';
import { API_GET_CHECKLIST_HISTORY } from '../../../Util/URLConstants';
import { VM_Colors } from '../../../Styles/VMStyles';

function CheckListHistory({ id }) {
  // const sessionFactory = new StorageFactory('session');
  // const userID = sessionFactory.get(SESSION_USER_ID_KEY);

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const statusConfig = {
    pending: { color: 'warning', icon: <HourglassEmpty fontSize="small" /> },
    inprogress: { color: 'info', icon: <HourglassEmpty fontSize="small" /> },
    completed: { color: 'success', icon: <CheckCircle fontSize="small" /> },
  };

  // Function to derive status if null
  const deriveStatus = (points) => {
    const values = Object.values(points || {});
    if (values.length === 0) return 'pending';

    if (values.every(v => v === 'Yes' || v === 'No')) return 'completed';
    if (values.some(v => v === 'Yes' || v === 'No')) return 'inprogress';
    return 'pending';
  };

  useEffect(() => {
    if (!id) {
      setHistory([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setHistory([]);

    const fetchHistory = async () => {
      try {
        const res = await axios.get(`${API_GET_CHECKLIST_HISTORY}?checklist_id=${id}`);
        if (!cancelled) {
          setHistory(res.data.data || []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching checklist history:', err);
          setHistory([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchHistory();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 240,
          width: '100%',
          py: 6,
        }}
        aria-busy="true"
        aria-label="Loading checklist history"
      >
        <CircularProgress size={48} sx={{ color: VM_Colors.primary }} />
      </Box>
    );
  }

  if (history.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="body1" color="text.secondary">
          No checklist history available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={2}>
        {history.map((item) => {
          const status = item.checklist_status || deriveStatus(item.checklist_points_status);
          const statusInfo = statusConfig[status.toLowerCase()] || {};

          return (
            <Card
              key={item.id}
              variant="outlined"
              sx={{
                borderRadius: 2,
                '&:hover': { boxShadow: 3, borderColor: 'primary.main' },
              }}
            >
              <CardContent>
                {/* Header */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar
                      sx={{
                        bgcolor: `${statusInfo.color}.light`,
                        width: 32,
                        height: 32,
                      }}
                    >
                      {statusInfo.icon}
                    </Avatar>
                    <Chip
                      label={status.toUpperCase()}
                      color={statusInfo.color || 'default'}
                      size="small"
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(item.created_date).toLocaleString()}
                  </Typography>
                </Stack>

                {/* Updated By */}
                {item.updated_by && (
                  <Typography variant="caption" color="text.secondary">
                    Updated by: {item.updated_by}
                  </Typography>
                )}

                {/* Comments */}
                {item.comments && (
                  <Typography variant="body2" color="text.primary" mt={1} mb={1}>
                    {item.comments}
                  </Typography>
                )}

                {/* Checklist Points */}
                {item.checklist_points_status && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Stack spacing={0.5}>
                      {Object.entries(item.checklist_points_status).map(([point, value]) => (
                        <Typography
                          key={point}
                          variant="body2"
                          color={value === 'Yes' ? 'success.main' : value === 'No' ? 'error.main' : 'text.primary'}
                        >
                          {point}: {value}
                        </Typography>
                      ))}
                    </Stack>
                  </>
                )}

                {/* Image */}
                {item.image_url && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Box mt={1}>
                      <img
                        src={item.image_url}
                        alt="Checklist"
                        style={{
                          maxWidth: '100%',
                          borderRadius: 8,
                          border: '1px solid #ddd',
                        }}
                      />
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
}

export default CheckListHistory;
